import * as React from "react";
import { renderAppTabListOverflowMenu } from "./renderAppTabListOverflowMenu";
import { useAppTabListOverflowMenu } from "./useAppTabListOverflowMenu";
import { useAppTabListOverflowMenuStyles } from "./useAppTabListOverflowMenuStyles.styles";
import type { AppTabListOverflowMenuProps } from "./AppTabListOverflowMenu.types";
import { customArrayCompare } from "@1js/acui-common";
import { shallowCompare } from "@fluentui/react/lib/Utilities";

/**
 * A menu for selecting tabs that have overflowed and are not visible.
 */
export const AppTabListOverflowMenu = React.memo(
  React.forwardRef<HTMLButtonElement, AppTabListOverflowMenuProps>(
    (props, ref) => {
      const state = useAppTabListOverflowMenu(props, ref);
      useAppTabListOverflowMenuStyles(state);
      return renderAppTabListOverflowMenu(state, props);
    }
  ),
  (prevProps, nextProps) => {
    const {
      topItems: prevTopItems,
      overflowItems: prevOverflowItems,
      ...prevRest
    } = prevProps;
    const {
      topItems: nextTopItems,
      overflowItems: nextOverflowItems,
      ...nextRest
    } = nextProps;
    // Perform a shallow comparison on the rest of the properties
    const shallowComparisonResult = shallowCompare(prevRest, nextRest);
    // If the shallow comparison fails, we can skip the deep comparison
    if (!shallowComparisonResult) return false;
    const selectedProps = new Set(["keytipProps"]);
    // Perform a deep comparison on the selected properties and rest by reference of the tab headers
    const overflowItemsComparisonResult = customArrayCompare(selectedProps)(
      prevOverflowItems,
      nextOverflowItems
    );
    if (!overflowItemsComparisonResult) return false;
    return customArrayCompare(selectedProps)(prevTopItems, nextTopItems);
  }
);

AppTabListOverflowMenu.displayName = "next/AppTabListOverflowMenu";
